<template>
  <div class="flight-booking-detail" :aria-busy="loading">
    <app-spinner v-if="loading" />
    <template v-else-if="booking">
      <header class="flight-booking-detail__header">
        <div class="flight-booking-detail__header-row">
          <div class="flight-booking-detail__heading">
            <span class="flight-booking-detail__logo-slot" :class="{ 'flight-booking-detail__logo-slot--wordmark': airline.name === 'Air Tanzania' }" aria-hidden="true">
              <img v-if="airline.logo_url && airline.logo_url !== failedLogo" :src="airline.logo_url" alt="" @error="failedLogo = airline.logo_url" />
              <span v-else class="flight-booking-detail__logo-fallback">{{ airlineInitial }}</span>
            </span>
            <h1 class="title">{{ airline.name || '—' }} <span>#{{ booking.id }}</span></h1>
            <span class="flight-booking-detail__status" :style="statusStyle">{{ label(booking.status) || '—' }}</span>
          </div>
          <a v-if="isSuperUser" :href="djangoUrl" target="_blank" rel="noopener noreferrer" class="flight-booking-detail__django btn btn--blue btn--small">Django</a>
        </div>
        <div v-if="booking.lead || booking.group_name" class="flight-booking-detail__context">
          <span>{{ booking.lead ? 'Lead:' : 'Group:' }}</span>
          <span v-if="manager" class="flight-booking-detail__avatar" role="img" :aria-label="`Manager: ${managerName || '—'}`" :title="`Manager: ${managerName || '—'}`">
            <img v-if="managerPhoto && managerPhoto !== failedPhoto" :src="managerPhoto" alt="" @error="failedPhoto = managerPhoto" />
            <span v-else aria-hidden="true">{{ managerInitials }}</span>
          </span>
          <router-link v-if="booking.lead && canOpenLead" :to="{ name: 'leads-detail-reservations', params: { id: booking.lead.id } }">{{ booking.lead.name || `Lead #${booking.lead.id}` }}</router-link>
          <strong v-else>{{ booking.lead ? booking.lead.name || `Lead #${booking.lead.id}` : booking.group_name }}</strong>
        </div>
        <div class="flight-booking-detail__summary-row" :class="{ 'flight-booking-detail__summary-row--actions': showAutoActions || showManualActions }">
          <span class="flight-booking-detail__paper-pattern" aria-hidden="true" />
          <dl class="flight-booking-detail__summary" aria-label="Booking summary">
            <div v-for="field in summaryFields" :key="field.name">
              <dt :class="{ 'flight-booking-detail__pnr-label': field.name === 'PNR' }">{{ field.name }}<app-button v-if="field.name === 'PNR' && canEditPnr" ref="editPnr" class="flight-booking-detail__pnr-edit" theme="empty" title="Edit PNR" aria-label="Edit PNR" @click.native="openPnrModal"><i class="pi pi-pencil" aria-hidden="true" /></app-button></dt>
              <dd><span v-if="field.status" class="flight-booking-detail__status" :style="badgeStyle(field.value)">{{ label(field.value) || '—' }}</span><template v-else>{{ empty(label(field.value)) ? '—' : label(field.value) }}</template></dd>
            </div>
          </dl>
          <div v-if="showAutoActions || showManualActions" class="flight-booking-detail__ticket-actions">
            <booking-auto-actions :key="booking.id" :booking="booking" @updated="updateBooking" @status-change="updateStatus" />
            <booking-manual-actions :key="`manual-${booking.id}`" :booking="booking" @updated="updateBooking" />
          </div>
        </div>
      </header>
      <nav aria-label="Booking sections">
        <app-tabs :tabs="tabs" />
      </nav>
      <booking-edit-modal :key="`pnr-${booking.id}`" :name="pnrModalName" :booking="booking" block="pnr" @updated="updateBooking" @closed="focusPnrButton" />
      <router-view :key="booking.id" :booking="booking" :passengers-refresh-key="bookingRevision" @updated="updateBooking" @assignment-change="$set(booking, 'passengers_assignment', $event)" />
    </template>
    <div v-else class="flight-booking-detail__error" role="alert">
      <app-no-results title="Unable to load booking" :text="loadError || 'Booking not found'" />
      <app-cells :indent="false" position="center" gap="12px">
        <app-button theme="dark" @click.native="loadBooking">Retry</app-button>
        <app-button @click.native="$router.push({ name: 'flights-bookings' })">Flight bookings</app-button>
      </app-cells>
    </div>
  </div>
</template>

<script>
import { getFlightsBooking } from '@/http'
import AppTabs from '@/components/AppTabs'
import BookingEditModal from './BookingEditModal.vue'
import { canEditBookingBlock } from './booking-edits'
import BookingAutoActions from './BookingAutoActions.vue'
import BookingManualActions from './BookingManualActions.vue'
import { bookingManualActions } from './booking-manual-actions'
import { code, label, empty, hasBookingSalesRole, responseError } from './booking-detail'

export default {
  name: 'FlightBookingDetail',
  components: { AppTabs, BookingAutoActions, BookingManualActions, BookingEditModal },
  data() {
    return {
      booking: null,
      bookingRevision: 0,
      loading: true,
      loadError: '',
      requestId: 0,
      failedLogo: '',
      failedPhoto: '',
      tabs: [
        { name: 'flights-bookings-detail-overview', label: 'Overview' },
        { name: 'flights-bookings-detail-finance', label: 'Finance' },
        { name: 'flights-bookings-detail-emails', label: 'E-mails' },
        { name: 'flights-bookings-detail-history', label: 'History' }
      ]
    }
  },
  computed: {
    isSuperUser() { return this.$store.state.userData.is_superuser },
    djangoUrl() { return `${process.env.VUE_APP_ADMIN_URL}flights/flightbooking/${this.booking.id}/change/` },
    canEditPnr() { return canEditBookingBlock(this.booking, this.$store.state.userGroups, 'pnr') },
    pnrModalName() { return `edit-booking-pnr-${this.booking.id}` },
    showAutoActions() { return code(this.booking.booking_mode) === 'aerocrs' && hasBookingSalesRole(this.$store.state.userGroups) },
    showManualActions() { return bookingManualActions(this.booking).length > 0 },
    canOpenLead() { return hasBookingSalesRole(this.$store.state.userGroups) },
    airline() { return this.booking.airline || {} },
    airlineInitial() { return (this.airline.name || '').trim().charAt(0).toUpperCase() || '—' },
    manager() { return this.booking.lead && this.booking.lead.manager },
    managerName() {
      return this.manager ? (this.manager.full_name || [this.manager.first_name, this.manager.last_name].filter(Boolean).join(' ')).trim() : ''
    },
    managerPhoto() { return (this.manager && this.manager.crop && this.manager.crop.crop_url) || '' },
    managerInitials() {
      const names = this.managerName.split(/\s+/).filter(Boolean)
      return names.length ? [names[0][0], names.length > 1 ? names[names.length - 1][0] : ''].join('').toUpperCase() : '—'
    },
    statusStyle() {
      const color = this.booking.status && this.booking.status.color
      return color ? { backgroundColor: color, color: '#fff' } : null
    },
    summaryFields() {
      const fields = [
        { name: 'Execution', value: this.booking.booking_mode },
        { name: 'PNR', value: this.booking.pnr }
      ]
      if (code(this.booking.booking_mode) === 'manual') {
        const manual = this.booking.manual_booking || {}
        fields.push(
          { name: 'Sales request', value: manual.request_status, status: true },
          { name: 'Airline response', value: manual.provider_status, status: true }
        )
      } else if (code(this.booking.booking_mode) === 'aerocrs') {
        fields.push({ name: 'Booking ID', value: (this.booking.aerocrs_booking || {}).provider_booking_id })
      }
      return fields
    }
  },
  watch: {
    '$route.params.id'() { this.loadBooking() }
  },
  created() { this.loadBooking() },
  beforeDestroy() { this.requestId++ },
  methods: {
    label,
    empty,
    openPnrModal() { if (this.canEditPnr) this.$modal.show(this.pnrModalName) },
    focusPnrButton() { const button = this.$refs.editPnr && this.$refs.editPnr[0]; if (button) button.$el.focus() },
    badgeStyle(value) { return value && value.color ? { backgroundColor: value.color, color: '#fff' } : null },
    updateBooking(booking) {
      if (this.booking && booking.id === this.booking.id) {
        this.booking = booking
        this.bookingRevision++
      }
    },
    updateStatus({ id, status }) {
      if (this.booking && id === this.booking.id) this.$set(this.booking, 'status', status)
    },
    async loadBooking() {
      const requestId = ++this.requestId
      this.loading = true
      this.booking = null
      this.loadError = ''
      this.failedLogo = ''
      this.failedPhoto = ''
      try {
        const { data } = await getFlightsBooking(this.$route.params.id, { localErrors: true })
        if (requestId !== this.requestId) return
        this.booking = data && data.id ? data : null
      } catch (error) {
        if (requestId === this.requestId) this.loadError = responseError(error, 'Unable to load booking')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.flight-booking-detail {
  min-width: 0;
  max-width: 1050px;
  padding-bottom: 48px;
  color: #20241d;
  font: 400 15px/1.45 $font-family-base;
}
.flight-booking-detail__header { margin-bottom: 24px; }
.flight-booking-detail__header-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.flight-booking-detail__django { flex-shrink: 0; min-height: auto; }
.flight-booking-detail__heading { display: flex; align-items: center; flex-wrap: wrap; gap: 10px 12px; }
.flight-booking-detail__heading .title { margin: 0; min-width: 0; overflow-wrap: anywhere; }
.flight-booking-detail__heading .title span { white-space: nowrap; }
.flight-booking-detail__logo-slot { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 32px; height: 32px; }
.flight-booking-detail__logo-slot--wordmark { width: 64px; }
.flight-booking-detail__logo-slot img { display: block; width: 100%; height: 100%; object-fit: contain; }
.flight-booking-detail__logo-fallback { display: grid; place-items: center; width: 28px; height: 28px; border: 1px solid #dce0d6; border-radius: 50%; background: #f7f8f5; color: #62685d; font-size: 13px; }
.flight-booking-detail__status { flex-shrink: 0; padding: 3px 8px; border-radius: 5px; background: #f7f8f5; color: #62685d; font-weight: 500; }
.flight-booking-detail__context {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 14px;
  min-width: 0;
  color: #62685d;
  > a, > strong { min-width: 0; overflow-wrap: anywhere; font-weight: 500; }
  > strong { color: #20241d; }
  > a { color: #527b12; text-decoration: none; }
  > a:hover { text-decoration: underline; }
  > a:focus-visible { outline: 2px solid #527b12; outline-offset: 3px; }
}
.flight-booking-detail__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  overflow: hidden;
  border: 1px solid #dce0d6;
  border-radius: 50%;
  background: #f0f6e7;
  color: #527b12;
  font-size: 10px;
  img { width: 100%; height: 100%; object-fit: cover; object-position: 50% 30%; }
}
.flight-booking-detail__summary-row {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  margin-top: 24px;
  min-height: 104px;
  border: 1px solid #e2e0d7;
  border-radius: 6px;
  background: #fcfbf6;
  // The cuts decorate the paper only: the Actions menu must remain unclipped.
  &::before, &::after {
    content: '';
    position: absolute;
    top: 6px;
    bottom: 6px;
    z-index: 1;
    width: 7px;
    background: radial-gradient(circle at 100% 50%, #fff 3px, #e2e0d7 3.5px, transparent 4px) 0 0 / 7px 12px repeat-y;
    pointer-events: none;
  }
  &::before { left: -1px; transform: rotate(180deg); }
  &::after { right: -1px; }
}
.flight-booking-detail__summary-row--actions {
  grid-template-columns: minmax(0, 1fr) 236px;
  &::before { display: none; }
}
.flight-booking-detail__paper-pattern {
  position: absolute;
  right: 1px;
  bottom: 1px;
  left: 1px;
  height: 28px;
  border-radius: 0 0 5px 5px;
  background: url('~@/assets/img/flight-booking-paper-pattern.png') left bottom / 192px 192px repeat-x;
  opacity: .65;
  pointer-events: none;
}
.flight-booking-detail__summary {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 24px 12px 28px;
  min-width: 0;
  > div { flex: 1 1 0; min-width: 0; padding: 0 24px; border-left: 1px dashed #deddd4; }
  > div:first-child { border-left: 0; }
  dt { color: #62685d; margin-bottom: 5px; }
  dd { margin: 0; font-size: 17px; font-weight: 500; overflow-wrap: anywhere; }
  .flight-booking-detail__status { font-size: 15px; }
}
.flight-booking-detail__pnr-label { display: flex; align-items: center; }
.flight-booking-detail__pnr-edit.btn { display: inline-flex; margin-left: 8px; padding: 2px 4px; min-width: 24px; min-height: 24px; border: 1px solid $color-border; border-radius: 4px; background: #fff; color: #20241d; font-size: 12px; }
.flight-booking-detail__pnr-edit:focus-visible { outline: 2px solid $color-theme; outline-offset: 2px; }
.flight-booking-detail__ticket-actions {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border-left: 1px dashed #d5d2c5;
  &::before, &::after {
    content: '';
    position: absolute;
    left: -10px;
    width: 18px;
    height: 18px;
    border: 1px solid #e2e0d7;
    border-radius: 50%;
    background: #fff;
    pointer-events: none;
  }
  &::before { top: -10px; clip-path: inset(50% 0 0); }
  &::after { bottom: -10px; clip-path: inset(0 0 50%); }
  ::v-deep .app-actions-menu__toggle {
    min-width: 160px;
    min-height: 44px;
    padding: 10px 16px;
    font-size: 14px;
    gap: 12px;
  }
}
.flight-booking-detail__error { display: grid; justify-items: center; gap: 16px; }
@media (min-width: 761px) and (max-width: 1000px) {
  .flight-booking-detail__summary-row--actions .flight-booking-detail__summary {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    padding: 24px;
    > div { padding: 0; border: 0; }
  }
}
@media (max-width: 760px) {
  .flight-booking-detail__summary-row--actions { grid-template-columns: minmax(0, 1fr); }
  .flight-booking-detail__summary {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    padding: 24px;
    > div { padding: 0; border: 0; }
  }
  .flight-booking-detail__ticket-actions {
    justify-content: flex-end;
    padding: 18px 24px;
    border-left: 0;
    border-top: 1px dashed #d5d2c5;
    &::before { top: -10px; left: -10px; clip-path: inset(0 0 0 50%); }
    &::after { top: -10px; right: -10px; bottom: auto; left: auto; clip-path: inset(0 50% 0 0); }
    ::v-deep .app-actions-menu__panel { left: auto; right: 0; }
  }
}
</style>
